import React from 'react';
import { PolicyTemplate } from '../../../types/policy';

/**
 * Props interface for the BasicInfoTab component
 * @property policy - The current policy template being edited
 * @property selectedEffect - Currently selected policy effect
 * @property onDisplayNameChange - Callback for updating display name
 * @property onDescriptionChange - Callback for updating description
 * @property onCategoryChange - Callback for updating category
 * @property onModeChange - Callback for updating policy mode
 * @property onEffectChange - Callback for updating policy effect
 */
interface BasicInfoTabProps {
  policy: PolicyTemplate;
  selectedEffect: string;
  onDisplayNameChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onCategoryChange: (value: string) => void;
  onModeChange: (value: string) => void;
  onEffectChange: (value: string) => void;
}

/**
 * BasicInfoTab component for editing basic policy information
 * 
 * This component provides form fields for editing fundamental policy attributes:
 * - Display Name: Human-readable name for the policy
 * - Description: Detailed explanation of the policy's purpose
 * - Category: Policy classification (Security, Monitoring, etc.)
 * - Mode: Policy evaluation mode (All or Indexed)
 * - Effect: Policy enforcement behavior (Audit, Deny, etc.)
 * 
 * @example
 * <BasicInfoTab
 *   policy={currentPolicy}
 *   selectedEffect="audit"
 *   onDisplayNameChange={handleDisplayNameChange}
 *   onDescriptionChange={handleDescriptionChange}
 *   onCategoryChange={handleCategoryChange}
 *   onModeChange={handleModeChange}
 *   onEffectChange={handleEffectChange}
 * />
 */
export const BasicInfoTab: React.FC<BasicInfoTabProps> = ({
  policy,
  selectedEffect,
  onDisplayNameChange,
  onDescriptionChange,
  onCategoryChange,
  onModeChange,
  onEffectChange
}) => {
  return (
    <div className="flex flex-col space-y-6 w-full min-w-0 border border-gray-200 dark:border-gray-700 rounded-lg p-6 shadow-sm">
      <div className="space-y-4 w-full">
        {/* Display Name input */}
        <div>
          <label htmlFor="displayName" className="block mb-1 text-sm font-medium text-gray-900 dark:text-gray-100">Display Name</label>
          <input
            id="displayName"
            type="text"
            className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={policy.properties.displayName}
            onChange={(e) => onDisplayNameChange(e.target.value)}
            placeholder="Enter display name"
          />
        </div>
        
        {/* Description textarea */}
        <div>
          <label htmlFor="description" className="block mb-1 text-sm font-medium text-gray-900 dark:text-gray-100">Description</label>
          <textarea
            id="description"
            className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={policy.properties.description}
            onChange={(e) => onDescriptionChange(e.target.value)}
            placeholder="Enter description"
            rows={4}
          />
        </div>
        
        {/* Category selection */}
        <div>
          <label htmlFor="category" className="block mb-1 text-sm font-medium text-gray-900 dark:text-gray-100">Category</label>
          <select
            id="category"
            className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={policy.properties.metadata.category}
            onChange={(e) => onCategoryChange(e.target.value)}
          >
            <option value="">Select Category</option>
            <option value="Security">Security</option>
            <option value="Monitoring">Monitoring</option>
            <option value="Backup">Backup</option>
            <option value="Storage">Storage</option>
            <option value="Network">Network</option>
            <option value="Compute">Compute</option>
            <option value="General">General</option>
          </select>
        </div>
        
        {/* Mode selection */}
        <div>
          <label htmlFor="mode" className="block mb-1 text-sm font-medium text-gray-900 dark:text-gray-100">Mode</label>
          <select
            id="mode"
            className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={policy.properties.mode}
            onChange={(e) => onModeChange(e.target.value)}
          >
            <option value="All">All</option>
            <option value="Indexed">Indexed</option>
          </select>
        </div>

        {/* Effect selection */}
        <div>
          <label htmlFor="effect" className="block mb-1 text-sm font-medium text-gray-900 dark:text-gray-100">Effect</label>
          <select
            id="effect"
            className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={selectedEffect}
            onChange={(e) => onEffectChange(e.target.value)}
          >
            <option value="audit">Audit</option>
            <option value="deny">Deny</option>
            <option value="modify">Modify</option>
            <option value="auditIfNotExists">Audit If Not Exists</option>
            <option value="deployIfNotExists">Deploy If Not Exists</option>
            <option value="disabled">Disabled</option>
          </select>
        </div>
      </div>
    </div>
  );
}; 